import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import differenceInDays from 'date-fns/differenceInDays';
import BillStatusBadge from '../../../components/BillStatusBadge';
import { DueDateNotice } from '../../../../../../../components/DueDateNotice';
import { IBillItem } from '../../../../../types';
import { convertValueToUSD } from '../../../../../../../helpers/currency.helper';
import { formatDateV2, getNumOfOverdueDays } from '../../../../../../../helpers/datesAndTime.helper';
import { isBillOverdue } from '../../../utils';
import BillPreviewActions from './BillPreviewActions';
import { ReactComponent as ReminderIcon } from '../../../../../../../assets/icons/svg/shared/ic-shared-reminder-on.svg';
import { ReactComponent as CircledSwitchIcon } from '../../../../../../../assets/icons/svg/shared/ic-shared-circled-switch.svg';
import { getBillRecurringEventTypeText, isOneTimeBill, MIN_DAYS_FOR_REMINDER } from '../../../../../BillDataUtils';
import { featureAccess, LimitedFeatureType } from '../../../../../../../services/appAccess.service';
import { FEATURE_FLAG } from '../../../../../../../constants/general';
import { FeatureAccess } from '../../../../../../AccountSettings/components/rolesAndPermissions/PermissionsList';

interface IBillDetailsContentProps {
  billItem: IBillItem;
}

const BillPreviewContent = ({ billItem }: IBillDetailsContentProps) => {
  const { t } = useTranslation();
  const {
    calculatedStatus,
    dueDate,
    paidTime,
    supplierName,
    amount,
    billId,
    recurringPaymentData,
    paymentReminderData,
  } = billItem;
  const billRecipientTitle = `${t('BILL_PAY.BILL_PREVIEW.BILL_TO')}`;
  const billAmountText = useMemo(() => convertValueToUSD(amount, 2), [amount]);
  const billDateText = `${formatDateV2(dueDate, true)}`;
  const isBillOverdueVal = isBillOverdue(calculatedStatus, getNumOfOverdueDays(dueDate));
  const isReminderOptionEnabled = differenceInDays(new Date(dueDate), new Date()) >= MIN_DAYS_FOR_REMINDER;
  const billRecurringTitle = `${t('BILL_PAY.BILL_PREVIEW.RECURRING')}`;
  const billPayPermission = featureAccess(FEATURE_FLAG.BILL_PAY, LimitedFeatureType.BillPay);

  return (
    <div>
      <div className='px-[24px] pt-[12px] pb-[16px] flex flex-col border-b-[1px] border-bw03'>
        <div className='pb-[12px] border-b-[1px] border-bw03 text-bw07 font-metricR text-sm flex items-center justify-between'>
          <div>
            {billRecipientTitle}
            <span className='text-bw08 font-metricRMedium ml-[5px] inline-block'>{supplierName}</span>
          </div>
          {calculatedStatus && <BillStatusBadge status={calculatedStatus} testId='BillsHub__BillPreview__status' />}
        </div>
        <div className='flex items-center justify-between mt-[12px]'>
          <div className='flex'>
            {paymentReminderData?.paymentReminderEnabled && <ReminderIcon className='mr-[3px]' />}
            <div className='text-sm text-bw08 font-metricR leading-5 mr-[4px]'>{billDateText}</div>
            <DueDateNotice status={calculatedStatus} dueDate={dueDate} paidTime={paidTime} />
          </div>
          <div className={`text-lg text-bw08 font-metricRSemibold leading-7 ${isBillOverdueVal && 'text-red02'}`}>
            {billAmountText}
          </div>
        </div>
        {recurringPaymentData &&
          recurringPaymentData.paymentFrequency &&
          !isOneTimeBill(recurringPaymentData.paymentFrequency) && (
            <div className='flex items-center'>
              <CircledSwitchIcon className='mr-[3px]' />
              <div className='text-sm text-bw07 font-metricR leading-5 mr-[4px]'>{`${billRecurringTitle}-${getBillRecurringEventTypeText(recurringPaymentData.paymentFrequency)}`}</div>
              <div className='text-sm text-bw07 font-metricRSemibold leading-5 mr-[4px]'>{`${recurringPaymentData.recurringIndex} of ${recurringPaymentData.recurringNumber}`}</div>
            </div>
        )}
        {billPayPermission === FeatureAccess.READ_WRITE && (
          <BillPreviewActions
            status={calculatedStatus}
            billId={billId}
            paymentReminderOn={paymentReminderData?.paymentReminderEnabled ?? false}
            isReminderOptionEnabled={isReminderOptionEnabled}
          />
        )}
      </div>
    </div>
  );
};

export default BillPreviewContent;
